import { ThunkDispatch } from "@reduxjs/toolkit";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { MaxRegenCount, MaxPartialRegenCount } from "../../constants/index";
import { itemRemovalActions, ItemImage } from "../../reducers/itemRemoval";
import { selectCanProcessItemRemoval, selectCanRegenAfterImages } from "../../selectors";
import { GenerateAfterPhotoIcon, ReloadRoomDesignIcon } from "../SVGIcons";

function mapStateToProps(state: any) {
  const canProcessItemRemoval = selectCanProcessItemRemoval(state)
  const canConfirmReloadItemRemoval = state.itemRemoval.itemImages.length > 0

  // 直接itemImagesから選択された画像を取得
  const { itemImages, selectedItemImage, furnitureRemovalMode, furnitureMaskPreviewBlob } = state.itemRemoval;
  const selectedItemImageData = selectedItemImage
    ? itemImages.find((img: any) => img.stubId === selectedItemImage)
    : (itemImages.length > 0 ? itemImages[0] : null);

  // 再実行ボタンの有効/無効状態を直接計算
  const canRegenAfterImages = selectedItemImageData &&
    selectedItemImageData.id &&
    selectedItemImageData.maxRegenCount > 0 &&
    !state.itemRemoval.isLoading;

  // 一部モードでプレビューがある場合は実行可能
  const isPartialModeWithPreview = furnitureRemovalMode === 'partial' && furnitureMaskPreviewBlob;

  // 未処理の画像数を計算
  const unprocessedCount = itemImages.filter((img: any) => !img.isProcessed).length;

  return {
    ...state.itemRemoval,
    canProcessItemRemoval,
    canConfirmReloadItemRemoval,
    canRegenAfterImages,
    selectedItemImageData,
    furnitureRemovalMode,
    isPartialModeWithPreview,
    unprocessedCount,
  }
}

interface ItemRemovalEditActionButtonsProps {
  itemImages: ItemImage[];
  canProcessItemRemoval: boolean;
  canConfirmReloadItemRemoval: boolean;
  canRegenAfterImages: boolean;
  selectedItemImageData?: ItemImage;
  isLoading: boolean;
  furnitureRemovalMode: 'full' | 'partial';
  isPartialModeWithPreview: boolean;
  unprocessedCount: number;
}

const ItemRemovalEditActionButtons = (props: ItemRemovalEditActionButtonsProps) => {
  const {
    itemImages,
    canProcessItemRemoval,
    canConfirmReloadItemRemoval,
    canRegenAfterImages,
    selectedItemImageData,
    isLoading,
    furnitureRemovalMode,
    isPartialModeWithPreview,
    unprocessedCount
  } = props
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
  const navigate = useNavigate()
  const {id, isProcessedFail, isProcessed} = selectedItemImageData ? selectedItemImageData : {}

  // サーバーからの応答があったかどうかを判定
  const hasServerResponse = selectedItemImageData && (isProcessed || isProcessedFail)

  const confirmProcessItemRemoval = () => {
    // 一部モードでも全体モードでも同じフロー（確認モーダル→processItemRemoval）
    // processItemRemoval内で各画像のmaskDataを見てAPIを呼び分ける
    if (!canProcessItemRemoval) return

    dispatch(itemRemovalActions.setIsConfirmPopupShow(true))
  }

  const confirmRegenAfterImages = () => {
    if (!canRegenAfterImages) return

    dispatch(itemRemovalActions.setIsRegenPopupShow(true))
  }

  const confirmClearAll = () => {
    if (!canConfirmReloadItemRemoval) return

    dispatch(itemRemovalActions.setIsClearAllPopupShow(true))
  }

  return (
    <div className="grid grid-cols-9 gap-4 mt-3">
      {/* AI家具消し実行ボタンは常に表示 */}
      <div className="col-span-4 col-start-2 space-y-2">
        <button
          className={
            canProcessItemRemoval && !isProcessedFail && unprocessedCount > 0 && !isLoading
              ? "px-5 py-2.5 flex items-center gap-1 rounded-full mx-auto text-sm text-white hover:text-white submit-btn hover:submit-btn"
              : "px-5 py-2.5 flex items-center gap-1 rounded-full mx-auto text-sm text-gray-900 bg-gray-400 disabled:opacity-75"
          }
          disabled={
            !canProcessItemRemoval ||
            isProcessedFail ||
            isLoading ||
            !selectedItemImageData ||
            unprocessedCount === 0
          }
          type="button"
          onClick={confirmProcessItemRemoval}
        >
          <GenerateAfterPhotoIcon />
          AI家具消し実行
          <span className="inline-flex items-center justify-center w-4 h-4 ms-2 text-xs font-semibold text-blue-800 submit-subtext rounded-full">
            {unprocessedCount}
          </span>
        </button>
      </div>
      {/* 再実行ボタンと新規画像実行ボタンはサーバーからの応答があった場合のみ表示 */}
      {itemImages.length > 0 && hasServerResponse && (
        <div className="col-span-4 space-x-2 flex justify-center">
          {/* 処理済みでない場合のみ再実行ボタンを表示 */}
          {!id && !isProcessed && (
            <button
              className={canProcessItemRemoval && !isLoading
                ? "px-5 py-2.5 flex items-center gap-1 rounded-full text-sm text-white justify-center submit-btn-2nd hover:bg-gray-100 w-full max-w-64"
                : "px-5 py-2.5 flex items-center gap-1 rounded-full text-sm text-white justify-center text-gray-900 bg-gray-400 disabled:opacity-75 w-full max-w-64"
              }
              disabled={!canProcessItemRemoval || isLoading}
              type="button"
              onClick={confirmProcessItemRemoval}
            >
              <ReloadRoomDesignIcon />
              再実行
            </button>
          )}
          {/* 処理済み、または処理失敗の場合に再実行ボタンを表示 */}
          {id && (isProcessed || isProcessedFail) && (
            <button
              className={canRegenAfterImages
                ? "px-5 py-2.5 flex items-center gap-1 rounded-full text-sm justify-center submit-btn-2nd hover:bg-gray-100 w-full max-w-64"
                : "px-5 py-2.5 flex items-center gap-1 rounded-full text-sm text-white justify-center text-gray-900 bg-gray-400 disabled:opacity-75 w-full max-w-64"
              }
              disabled={!canRegenAfterImages}
              type="button"
              onClick={confirmRegenAfterImages}
            >
              <ReloadRoomDesignIcon />
              {selectedItemImageData.maxRegenCount == (selectedItemImageData.processedWithPartialMode ? MaxPartialRegenCount : MaxRegenCount) ? "再実行" : `再実行（残り${selectedItemImageData.maxRegenCount}回）`}
            </button>
          )}
          {/* 新規画像を実行ボタンは常に表示 */}
          <button
            className={canConfirmReloadItemRemoval && !isLoading
              ? "px-5 py-2.5 flex items-center gap-1 rounded-full text-sm text-white justify-center hover:text-white submit-btn hover:submit-btn w-full max-w-64"
              : "px-5 py-2.5 flex items-center gap-1 rounded-full text-sm text-white justify-center text-gray-900 bg-gray-400 disabled:opacity-75 w-full max-w-64"
            }
            disabled={!canConfirmReloadItemRemoval || isLoading}
            type="button"
            onClick={confirmClearAll}
          >
            <GenerateAfterPhotoIcon />
            新規画像を実行
          </button>
        </div>
      )}
    </div>
  )
};

export default connect(mapStateToProps)(ItemRemovalEditActionButtons);